module Main exposing (main)

import Api
import Api.Endpoint as Endpoint exposing (Endpoint)
import Api.Fulltext exposing (Fulltext)
import Api.Metadata exposing (Metadata)
import Api.Query as Query exposing (Query)
import Api.System exposing (System)
import Array exposing (Array)
import Browser
import Browser.Events as Ev
import Browser.Navigation as Nav
import Credentials exposing (Credentials)
import Date exposing (Date)
import Dict
import Dict.Extra as Dict
import Element exposing (..)
import Element.Font as Font
import Entity
import Entity.SecurityUser
import Flags exposing (Flags)
import Html.Attributes as Html
import Http
import I18Next exposing (..)
import I18n exposing (I18n)
import Json.Decode as Decode
import Json.Encode as Encode
import Lang exposing (Lang)
import List
import Maybe.Extra as Maybe
import Page.Dashboard as Dashboard
import Page.Login as Login
import Page.Tasks as Tasks
import Route exposing (Route)
import Session exposing (Session)
import String.Interpolate as String
import Task
import Time
import Ui.Theme exposing (Theme)
import Ui.Toast as Toast
import Ui.Toast.Kind as Toast
import Ui.ToastPort as Toast exposing (Toast)
import Update exposing (Update)
import Url exposing (Url)
import Url.Parser


main =
    Browser.application
        { init = init
        , update = update
        , view = view
        , subscriptions = subscriptions
        , onUrlRequest = UrlRequest
        , onUrlChange = UrlChange
        }



-- MODEL


type alias Model =
    { flags : Maybe Flags
    , lastToastId : Int
    , navKey : Nav.Key
    , page : Maybe Page
    , ready : Bool
    , receivedTranslations : Array (Maybe Translations)
    , toasts : List (Toast.Model Msg)
    , url : Url
    }


type Page
    = Empty Session
    | Login Login.Model
    | Dashboard Dashboard.Model
    | Tasks Tasks.Model


initModel : Flags -> Url -> Nav.Key -> Model
initModel flags url navKey =
    { lastToastId = 0
    , navKey = navKey
    , page = Nothing
    , flags = Just flags
    , ready = False
    , receivedTranslations = initReceivedTranslations flags
    , toasts = []
    , url = url
    }


initReceivedTranslations : Flags -> Array (Maybe Translations)
initReceivedTranslations flags =
    Array.repeat (List.length flags.i18n) Nothing


emptyModel : Url -> Nav.Key -> Model
emptyModel url navKey =
    { lastToastId = 0
    , navKey = navKey
    , page = Nothing
    , flags = Nothing
    , ready = False
    , receivedTranslations = Array.empty
    , toasts = []
    , url = url
    }


init : Encode.Value -> Url -> Nav.Key -> Update Model Msg
init encodedFlags url navKey =
    case Decode.decodeValue Flags.decoder encodedFlags of
        Err error ->
            Update.putModel (emptyModel url navKey)
                |> Update.addCmd (Toast.showError <| Decode.errorToString error)

        Ok flags ->
            ( initModel flags url navKey
            , loadAllTranslationsCmd True flags.lang flags.i18n
            )


isInitialized : Session -> Bool
isInitialized session =
    Maybe.isJust session.companyname
        && not (Dict.isEmpty session.controllers)
        && Maybe.isJust session.system
        && Maybe.isJust session.grouping
        && Maybe.isJust session.fulltexts
        && Maybe.isJust session.zone


afterInit : Model -> Update Model Msg
afterInit model =
    if Array.filter Maybe.isNothing model.receivedTranslations |> Array.isEmpty then
        case model.flags of
            Just flags ->
                let
                    translations =
                        Array.toList model.receivedTranslations |> Maybe.values

                    appName =
                        tf translations "Main.appName"

                    isCrossOrigin =
                        Maybe.unwrap False Endpoint.isCrossOrigin flags.endpoint
                in
                case Url.Parser.parse (Route.parser isCrossOrigin) model.url of
                    Nothing ->
                        Update.putModel model
                            |> Update.addCmd
                                (Toast.showError <|
                                    trf translations
                                        Curly
                                        "Main.badUrl"
                                        [ ( "url", Url.toString model.url ) ]
                                )

                    Just route ->
                        let
                            endpoint =
                                Maybe.map (Endpoint.setApp "tasks") <|
                                    case flags.endpoint of
                                        Just x ->
                                            Just x

                                        Nothing ->
                                            route.connection
                                                |> Maybe.map
                                                    (\connection ->
                                                        Endpoint.Absolute
                                                            { connection = connection
                                                            , app = Nothing
                                                            }
                                                    )
                        in
                        case endpoint of
                            Nothing ->
                                Update.putModel model
                                    |> Update.addCmd
                                        (Toast.showError <|
                                            tf translations "Main.badConnection"
                                        )

                            Just ep ->
                                let
                                    credentials =
                                        Just
                                            (\creds login ->
                                                if creds.name == login then
                                                    creds

                                                else
                                                    { name = login, password = "" }
                                            )
                                            |> Maybe.andMap flags.credentials
                                            |> Maybe.andMap route.login
                                            |> Maybe.orElse
                                                (route.login
                                                    |> Maybe.map
                                                        (\login ->
                                                            { name = login, password = "" }
                                                        )
                                                )
                                            |> Maybe.orElse flags.credentials

                                    session =
                                        { appName = appName
                                        , assets = flags.assets
                                        , credentials = credentials
                                        , companyname = Nothing
                                        , controllers = Dict.empty
                                        , device = classifyDevice flags.windowSize
                                        , endpoint = ep
                                        , fulltexts = Nothing
                                        , grouping = Nothing
                                        , lang = flags.lang
                                        , navKey = model.navKey
                                        , rootUrl = Route.toUrl <| Route.root route
                                        , route = route
                                        , system = Nothing
                                        , theme = theme
                                        , title = Nothing
                                        , today = Nothing
                                        , translations = translations
                                        , user = Nothing
                                        , windowSize = flags.windowSize
                                        , zone = Nothing
                                        }
                                in
                                Login.init session
                                    |> (\( m, cmds ) ->
                                            case credentials of
                                                Just _ ->
                                                    Login.update (Login.Login True) m
                                                        |> Update.map (putPage model << Login)

                                                Nothing ->
                                                    ( m, cmds )
                                                        |> Update.map (putPage model << Login)
                                                        |> Update.map (\m2 -> { m2 | ready = True })
                                       )
                                    |> Update.mapCmd LoginMsg
                                    |> Update.addCmd (Task.perform (AdjustDate True) Date.today)
                                    |> Update.addCmd (Task.perform AdjustTimeZone Time.here)
                                    |> Update.andThen clearUrl

            Nothing ->
                Update.putModel model

    else
        Update.putModel model



-- UPDATE


type Msg
    = AddToast Toast.Kind String Time.Posix
    | AdjustDate Bool Date
    | AdjustTimeZone Time.Zone
    | CheckToasts Time.Posix
    | CloseToast Int
    | CredentialsChange (Result String (Maybe Credentials))
    | DashboardMsg Dashboard.Msg
    | LangChange (Result String Lang)
    | LoadFulltextResult (Result String (List Fulltext))
    | LoadGroupingResult (Result String (List (Maybe Bool)))
    | LoadMetadataResult (Result String Metadata)
    | LoadSystemResult (Result String System)
    | LoginMsg Login.Msg
    | ReceiveToast (Result String Toast)
    | Refresh
    | TranslationsReceived Bool Lang Int (Result Http.Error Translations)
    | TasksMsg Tasks.Msg
    | UrlChange Url
    | UrlRequest Browser.UrlRequest
    | WindowResize Int Int


update : Msg -> Model -> Update Model Msg
update msg model =
    case msg of
        AddToast kind message time ->
            addToast kind message time model

        AdjustDate fromInit today ->
            adjustDate fromInit today model

        AdjustTimeZone zone ->
            adjustZone zone model

        CheckToasts time ->
            checkToasts time model

        CloseToast id ->
            closeToast id model

        CredentialsChange result ->
            credentialsChange result model

        DashboardMsg pageMsg ->
            dashboardMsg pageMsg model

        LangChange result ->
            langChange result model

        LoadFulltextResult result ->
            loadFulltextResult result model

        LoadGroupingResult result ->
            loadGroupingResult result model

        LoadMetadataResult result ->
            loadMetadataResult result model

        LoadSystemResult result ->
            loadSystemResult result model

        LoginMsg pageMsg ->
            loginPageMsg pageMsg model

        ReceiveToast result ->
            receiveToast result model

        Refresh ->
            refresh model

        TranslationsReceived inInit lang index result ->
            translationsReceived inInit lang index result model

        TasksMsg pageMsg ->
            tasksPageMsg pageMsg model

        UrlChange url ->
            urlChange url model

        UrlRequest request ->
            urlRequest request model

        WindowResize width height ->
            windowResize width height model


liftPageUpdate :
    (pageModel -> Page)
    -> (pageMsg -> Msg)
    -> Model
    -> Update pageModel pageMsg
    -> Update Model Msg
liftPageUpdate liftModel liftMsg model pageUpdate =
    Update.lift (putPage model << liftModel) liftMsg pageUpdate


addToast : Toast.Kind -> String -> Time.Posix -> Model -> Update Model Msg
addToast kind message time model =
    let
        id =
            model.lastToastId + 1

        toast =
            { kind = kind
            , id = String.fromInt id
            , message = message
            , time = time
            , onPress = Just <| CloseToast id
            }

        newModel =
            { model
                | toasts = toast :: model.toasts
            }
    in
    ( newModel, Cmd.none )


adjustDate : Bool -> Date -> Model -> Update Model Msg
adjustDate fromInit today model =
    let
        newModel =
            updateSession (\x -> { x | today = Just today }) model
    in
    Update.putModel newModel


adjustZone : Time.Zone -> Model -> Update Model Msg
adjustZone zone model =
    let
        newModel =
            updateSession (\x -> { x | zone = Just zone }) model
    in
    Update.putModel newModel


checkToasts : Time.Posix -> Model -> Update Model Msg
checkToasts time model =
    let
        millisToFade toast =
            case toast.kind of
                Toast.Error ->
                    -- error messages are visible longer than ordinal messages
                    theme.effects.long * 1000

                _ ->
                    theme.effects.short * 1000

        newToasts =
            List.filter
                (\toast ->
                    Time.posixToMillis time - Time.posixToMillis toast.time < millisToFade toast
                )
                model.toasts

        newModel =
            { model
                | toasts = newToasts
            }
    in
    ( newModel, Cmd.none )


closeToast : Int -> Model -> Update Model Msg
closeToast id model =
    let
        newToasts =
            List.filter (\x -> x.id /= String.fromInt id) model.toasts
    in
    ( { model | toasts = newToasts }, Cmd.none )


credentialsChange : Result String (Maybe Credentials) -> Model -> Update Model Msg
credentialsChange result model =
    case result of
        Err error ->
            Update.putModel model
                |> Update.addCmd (Toast.showError error)

        Ok credentials ->
            case getSession model of
                Just session ->
                    case credentials of
                        Nothing ->
                            { session | credentials = Nothing }
                                |> Login.init
                                |> liftPageUpdate Login LoginMsg model

                        Just cred ->
                            let
                                newSession =
                                    { session | credentials = Just cred }

                                newModel =
                                    putSession newSession model
                            in
                            Update.putModel newModel

                Nothing ->
                    ( model, Cmd.none )


dashboardMsg : Dashboard.Msg -> Model -> Update Model Msg
dashboardMsg pageMsg model =
    case model.page of
        Just (Dashboard pageModel) ->
            Dashboard.update pageMsg pageModel
                |> liftPageUpdate Dashboard DashboardMsg model

        _ ->
            Update.putModel model


initPage : Model -> Update Model Msg
initPage model =
    case getSession model of
        Just session ->
            if isInitialized session then
                let
                    newModel =
                        { model | ready = True }
                in
                case session.route.subRoute of
                    Route.Dashboard route ->
                        initDashboard session newModel

                    Route.Tasks route ->
                        initTasks session newModel

            else
                Update.putModel model

        Nothing ->
            Update.putModel model


initDashboard : Session -> Model -> Update Model Msg
initDashboard session model =
    Dashboard.init session
        |> liftPageUpdate Dashboard DashboardMsg model


initTasks : Session -> Model -> Update Model Msg
initTasks session model =
    Tasks.init session
        |> liftPageUpdate Tasks TasksMsg model


langChange : Result String Lang -> Model -> Update Model Msg
langChange result model =
    case result of
        Err error ->
            Update.putModel model
                |> Update.addCmd (Toast.showError error)

        Ok lang ->
            updateSession (\x -> { x | lang = lang }) model
                |> reloadTranslations lang


loadFulltextCmd : Session -> Cmd Msg
loadFulltextCmd session =
    Just Api.getFulltext
        |> Maybe.andMap (Just session.endpoint)
        |> Maybe.andMap session.credentials
        |> Maybe.andMap (Just LoadFulltextResult)
        |> Maybe.andMap (Just session.translations)
        |> Maybe.withDefault Cmd.none


loadFulltextResult : Result String (List Fulltext) -> Model -> Update Model Msg
loadFulltextResult result model =
    case result of
        Err error ->
            updateSession
                (\x ->
                    { x | fulltexts = Just Dict.empty }
                )
                model
                |> Update.putModel
                |> Update.addCmd (Toast.showError error)
                |> Update.andThen initPage

        Ok f ->
            let
                fulltexts =
                    Dict.fromListBy .boClsId f

                newModel =
                    updateSession
                        (\x ->
                            { x | fulltexts = Just fulltexts }
                        )
                        model
            in
            initPage newModel



-- TODO: Hack until API allows batch requests with selects


loadGroupingCmd : Session -> Cmd Msg
loadGroupingCmd session =
    let
        query =
            Query.empty
                |> Query.entityType Entity.entityGroupType
                |> Query.fields [ Query.field Entity.entityGroupField ]
                |> Query.take 1
    in
    Just Api.query
        |> Maybe.andMap (Just session.endpoint)
        |> Maybe.andMap session.credentials
        |> Maybe.andMap (Just query)
        |> Maybe.andMap (Just <| Entity.entityGroupDecoder)
        |> Maybe.andMap (Just LoadGroupingResult)
        |> Maybe.andMap (Just session.translations)
        |> Maybe.andMap (Just Nothing)
        |> Maybe.withDefault Cmd.none


loadGroupingResult : Result String (List (Maybe Bool)) -> Model -> Update Model Msg
loadGroupingResult result model =
    let
        grouping =
            case result of
                Ok (_ :: []) ->
                    True

                _ ->
                    False

        newModel =
            updateSession
                (\x ->
                    { x | grouping = Just grouping }
                )
                model
    in
    initPage newModel


loadMetadataCmd : Session -> Cmd Msg
loadMetadataCmd session =
    Just Api.getMetadata
        |> Maybe.andMap (Just session.endpoint)
        |> Maybe.andMap session.credentials
        |> Maybe.andMap (Just LoadMetadataResult)
        |> Maybe.andMap (Just session.translations)
        |> Maybe.withDefault Cmd.none


loadMetadataResult : Result String Metadata -> Model -> Update Model Msg
loadMetadataResult result model =
    case result of
        Err error ->
            Update.putModel model
                |> Update.addCmd (Toast.showError error)

        Ok metadata ->
            let
                controllers =
                    metadata.controllers
                        |> List.filter (not << String.isEmpty << .boName)
                        |> Dict.fromListBy .boClsId

                newModel =
                    updateSession
                        (\x ->
                            { x
                                | controllers = controllers
                                , companyname = Just metadata.companyname
                            }
                        )
                        model
            in
            initPage newModel


loadSystemCmd : Session -> Cmd Msg
loadSystemCmd session =
    Just Api.getSystem
        |> Maybe.andMap (Just session.endpoint)
        |> Maybe.andMap session.credentials
        |> Maybe.andMap (Just LoadSystemResult)
        |> Maybe.andMap (Just session.translations)
        |> Maybe.withDefault Cmd.none


loadSystemResult : Result String System -> Model -> Update Model Msg
loadSystemResult result model =
    case result of
        Err error ->
            Update.putModel model
                |> Update.addCmd (Toast.showError error)

        Ok system ->
            let
                newModel =
                    updateSession
                        (\x ->
                            { x
                                | system = Just system
                            }
                        )
                        model
            in
            initPage newModel


loginPageMsg : Login.Msg -> Model -> Update Model Msg
loginPageMsg pageMsg model =
    case model.page of
        Just (Login pageModel) ->
            case pageMsg of
                Login.LoginResult _ result ->
                    loginResult result model

                _ ->
                    Login.update pageMsg pageModel
                        |> liftPageUpdate Login LoginMsg model

        _ ->
            ( model, Cmd.none )


loginResult : Result String (List Entity.SecurityUser.Model) -> Model -> Update Model Msg
loginResult result model =
    case getSession model of
        Just session ->
            case result of
                Err error ->
                    let
                        newModel =
                            { model | ready = True }

                        newSession =
                            session.credentials
                                |> Maybe.andThen
                                    (\c ->
                                        Just
                                            { session
                                                | credentials =
                                                    Just { c | password = "" }
                                            }
                                    )
                                |> Maybe.withDefault { session | credentials = Nothing }
                    in
                    Login.init newSession
                        |> liftPageUpdate Login LoginMsg newModel
                        |> Update.addCmd
                            (Toast.showError error)

                Ok [ user ] ->
                    Update.putModel model
                        |> Update.map (updateSession (\x -> { x | user = Just user }))
                        |> Update.addCmd
                            (getSession model
                                |> Maybe.andThen (Just << loadFulltextCmd)
                                |> Maybe.withDefault Cmd.none
                            )
                        |> Update.addCmd
                            (getSession model
                                |> Maybe.andThen (Just << loadGroupingCmd)
                                |> Maybe.withDefault Cmd.none
                            )
                        |> Update.addCmd
                            (getSession model
                                |> Maybe.andThen (Just << loadMetadataCmd)
                                |> Maybe.withDefault Cmd.none
                            )
                        |> Update.addCmd
                            (getSession model
                                |> Maybe.andThen (Just << loadSystemCmd)
                                |> Maybe.withDefault Cmd.none
                            )
                        |> Update.andThen initPage

                _ ->
                    Update.putModel model

        Nothing ->
            Update.putModel model


receiveToast : Result String Toast -> Model -> Update Model Msg
receiveToast result model =
    case result of
        Err error ->
            Update.putModel model
                |> Update.addCmd (addToastCmd Toast.Error error)

        Ok notification ->
            Update.putModel model
                |> Update.addCmd (addToastCmd notification.kind notification.message)


tasksPageMsg : Tasks.Msg -> Model -> Update Model Msg
tasksPageMsg pageMsg model =
    case model.page of
        Just (Tasks pageModel) ->
            Tasks.update pageMsg pageModel
                |> liftPageUpdate Tasks TasksMsg model

        _ ->
            Update.putModel model


translationsReceived :
    Bool
    -> Lang
    -> Int
    -> Result Http.Error Translations
    -> Model
    -> Update Model Msg
translationsReceived inInit lang index result model =
    case result of
        Err error ->
            let
                message =
                    case error of
                        Http.BadBody x ->
                            x

                        _ ->
                            "Cannot load translations"
            in
            Update.putModel model
                |> Update.addCmd (Toast.showError message)

        Ok x ->
            let
                go : Model -> Update Model Msg
                go =
                    if inInit then
                        afterInit

                    else
                        putTranslations
            in
            go <|
                { model
                    | receivedTranslations =
                        Array.set index
                            (Just x)
                            model.receivedTranslations
                }


putTranslations : Model -> Update Model Msg
putTranslations model =
    let
        go : Session -> Session
        go session =
            { session | translations = Maybe.values <| Array.toList model.receivedTranslations }
    in
    Update.putModel <| updateSession go model


refresh : Model -> Update Model Msg
refresh model =
    ( model, Task.perform (AdjustDate False) Date.today )


urlChange : Url -> Model -> Update Model Msg
urlChange url model =
    case getSession model of
        Just session ->
            let
                isCrossOrigin =
                    Endpoint.isCrossOrigin session.endpoint
            in
            case Url.Parser.parse (Route.parser isCrossOrigin) url of
                Nothing ->
                    Update.putModel (updateSession (Session.updateTitle <| Just "404") model)
                        |> Update.addCmd
                            (Toast.showError <|
                                trf session.translations
                                    Curly
                                    "Main.badUrl"
                                    [ ( "url", Url.toString url ) ]
                            )

                Just route ->
                    if isInitialized session then
                        case route.subRoute of
                            Route.Dashboard _ ->
                                case model.page of
                                    Just (Dashboard pageModel) ->
                                        Dashboard.routeChange route pageModel
                                            |> liftPageUpdate Dashboard DashboardMsg model

                                    _ ->
                                        changePage initDashboard route model

                            Route.Tasks _ ->
                                case model.page of
                                    Just (Tasks pageModel) ->
                                        Tasks.routeChange route pageModel
                                            |> liftPageUpdate Tasks TasksMsg model

                                    _ ->
                                        changePage initTasks route model

                    else
                        Update.putModel model

        Nothing ->
            Update.putModel model


changePage : (Session -> Model -> Update Model Msg) -> Route -> Model -> Update Model Msg
changePage initNewPage route model =
    case getSession model of
        Just session ->
            let
                newSession =
                    { session
                        | rootUrl = Route.toUrl <| Route.root route
                        , route = route
                    }
            in
            case newSession.credentials of
                Nothing ->
                    Login.init newSession
                        |> liftPageUpdate Login LoginMsg model

                Just _ ->
                    initNewPage newSession model

        Nothing ->
            Update.putModel model


urlRequest : Browser.UrlRequest -> Model -> Update Model Msg
urlRequest request model =
    case getSession model of
        Just session ->
            case request of
                Browser.Internal url ->
                    let
                        isCrossOrigin =
                            Endpoint.isCrossOrigin session.endpoint
                    in
                    case Url.Parser.parse (Route.parser isCrossOrigin) url of
                        Nothing ->
                            Update.putModel model
                                |> Update.addCmd
                                    (Toast.showError <|
                                        trf session.translations
                                            Curly
                                            "Main.badUrl"
                                            [ ( "url", Url.toString url ) ]
                                    )

                        Just _ ->
                            ( model
                            , Nav.pushUrl model.navKey <| Url.toString url
                            )

                Browser.External url ->
                    ( model, Nav.pushUrl model.navKey url )

        Nothing ->
            ( model, Cmd.none )


windowResize : Int -> Int -> Model -> Update Model Msg
windowResize width height model =
    let
        windowSize =
            { width = width, height = height }

        newModel =
            updateSession
                (\session ->
                    { session
                        | windowSize = windowSize
                        , device = classifyDevice windowSize
                    }
                )
                model
    in
    ( newModel, Cmd.none )



-- SUBSCRIPTIONS


subscriptions : Model -> Sub Msg
subscriptions model =
    let
        checkToastsSub =
            if model.toasts == [] then
                Sub.none

            else
                Time.every 1000 CheckToasts

        pageSub =
            case model.page of
                Just (Empty _) ->
                    Sub.none

                Just (Login pageModel) ->
                    Login.subscriptions pageModel |> Sub.map LoginMsg

                Just (Dashboard pageModel) ->
                    Dashboard.subscriptions pageModel |> Sub.map DashboardMsg

                Just (Tasks pageModel) ->
                    Tasks.subscriptions pageModel |> Sub.map TasksMsg

                Nothing ->
                    Sub.none
    in
    Sub.batch
        [ Credentials.change CredentialsChange
        , Toast.receive ReceiveToast
        , checkToastsSub
        , Ev.onResize WindowResize
        , pageSub
        , Lang.change LangChange
        , Time.every (60 * 1000) <| always Refresh
        ]



-- VIEW


view : Model -> Browser.Document Msg
view model =
    { title =
        let
            session =
                getSession model

            appName =
                Maybe.unwrap "Tasks" .appName session
        in
        session
            |> Maybe.andThen .title
            |> Maybe.map
                (String.interpolate "{1} | {0}"
                    << (++) [ appName ]
                    << List.singleton
                )
            |> Maybe.withDefault
                appName
    , body =
        [ layoutWith layoutProps
            [ inFront <| viewToasts model
            , Font.size theme.fonts.fontSize
            , Font.color theme.fonts.fontColor
            , Font.family theme.fonts.default
            ]
          <|
            viewBody model
        ]
    }


viewBody : Model -> Element Msg
viewBody model =
    case model.ready of
        True ->
            row
                [ width fill
                , centerY
                , height fill
                ]
                [ viewPage model
                ]

        False ->
            viewLoading model


layoutProps : { options : List Option }
layoutProps =
    { options =
        [ focusStyle
            { borderColor = Nothing
            , backgroundColor = Nothing
            , shadow = Nothing
            }
        ]
    }



-- VIEW BODY


viewPage : Model -> Element Msg
viewPage model =
    case model.page of
        Just (Empty _) ->
            none

        Just (Login pageModel) ->
            Login.view pageModel |> Element.map LoginMsg

        Just (Dashboard pageModel) ->
            Dashboard.view pageModel |> Element.map DashboardMsg

        Just (Tasks pageModel) ->
            Tasks.view pageModel |> Element.map TasksMsg

        Nothing ->
            viewLoading model


viewLoading : Model -> Element Msg
viewLoading model =
    let
        session =
            getSession model

        size =
            case session |> Maybe.map .device |> Maybe.map .class of
                Just Phone ->
                    px 80

                _ ->
                    px 100

        logoEl =
            case model.flags |> Maybe.map .assets of
                Just assets ->
                    image
                        [ height size
                        , htmlAttribute <| Html.class theme.classes.showBeat
                        ]
                        { src = assets.abraLogoFragmentSvg
                        , description = "Logo"
                        }

                Nothing ->
                    none

        textEl =
            el
                [ centerX
                , Font.size <| theme.scaled 2
                ]
            <|
                text "Loading"
    in
    column
        [ centerX
        , centerY
        , spacing 40
        ]
        [ logoEl

        --        , textEl
        ]



-- VIEW TOASTS


viewToasts : Model -> Element Msg
viewToasts model =
    let
        attributes =
            [ alignTop
            , padding theme.spacing.inputY
            , spacing theme.spacing.modalX
            , centerX
            , htmlAttribute <| Html.class "toast-wrapper"
            ]
    in
    column attributes <|
        List.map
            (\toast ->
                Toast.view
                    { theme = theme
                    , assets = getSession model |> Maybe.map .assets
                    , model = toast
                    }
            )
            model.toasts



-- THEME


theme : Theme
theme =
    Ui.Theme.init



-- SESSION


getSession : Model -> Maybe Session
getSession model =
    model.page
        |> Maybe.map
            (\page ->
                case page of
                    Empty session ->
                        session

                    Login pageModel ->
                        Login.getSession pageModel

                    Dashboard pageModel ->
                        Dashboard.getSession pageModel

                    Tasks pageModel ->
                        Tasks.getSession pageModel
            )


putSession : Session -> Model -> Model
putSession session model =
    updateSession (always session) model


updateSession : (Session -> Session) -> Model -> Model
updateSession f model =
    case model.page of
        Just (Empty session) ->
            putPage model <| Empty <| f session

        Just (Login pageModel) ->
            putPage model <| Login <| Login.updateSession f pageModel

        Just (Dashboard pageModel) ->
            putPage model <| Dashboard <| Dashboard.updateSession f pageModel

        Just (Tasks pageModel) ->
            putPage model <| Tasks <| Tasks.updateSession f pageModel

        Nothing ->
            model


putPage : Model -> Page -> Model
putPage model page =
    { model | page = Just page }



-- COMMANDS


addToastCmd : Toast.Kind -> String -> Cmd Msg
addToastCmd kind message =
    Time.now |> Task.perform (AddToast kind message)


clearUrl : Model -> Update Model Msg
clearUrl model =
    let
        go =
            case getSession model of
                Just session ->
                    let
                        route =
                            session.route
                    in
                    if Maybe.isJust route.login then
                        Route.toUrl { route | login = Nothing }
                            |> Nav.pushUrl session.navKey

                    else
                        Cmd.none

                _ ->
                    Cmd.none
    in
    Update.putModel model
        |> Update.addCmd go


reloadTranslations : Lang -> Model -> Update Model Msg
reloadTranslations lang model =
    let
        go flags =
            Update.putModel { model | receivedTranslations = initReceivedTranslations flags }
                |> Update.addCmd (loadAllTranslationsCmd False lang flags.i18n)
    in
    Maybe.unwrap (Update.putModel model) go model.flags


loadAllTranslationsCmd : Bool -> Lang -> List I18n -> Cmd Msg
loadAllTranslationsCmd inInit lang i18n =
    Cmd.batch <| List.indexedMap (loadTranslationsCmd inInit lang) i18n


loadTranslationsCmd : Bool -> Lang -> Int -> I18n -> Cmd Msg
loadTranslationsCmd inInit lang index i18n =
    Http.get
        { url = Lang.getI18n lang i18n
        , expect =
            Http.expectJson
                (TranslationsReceived inInit lang index)
                I18Next.translationsDecoder
        }
